(() => {
/* Wild Gainz lab results flavor toggle.
   Progressive enhancement: without this script the first flavor's panel
   is visible (the others carry the hidden attribute) and the section CTA
   navigates to its fallback link instead of opening the modal.

   Markup contract (sections/wildgainz-transparency.liquid):
     <wildgainz-lab-results>
       <button data-lab-tab role="tab" aria-controls="...">Flavor</button>
       <div data-lab-panel role="tabpanel" hidden>...</div>
     </wildgainz-lab-results>
*/

class WildgainzLabResults extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-lab-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-lab-panel]'));
    if (this.panels.length === 0) return;

    // The dialog moves into <body> while open and back on close, so this
    // element reconnects on every open. Bind the tab handlers once.
    if (!this.__wgTabsBound) {
      this.__wgTabsBound = true;
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
      });
    }

    if (window.Shopify && window.Shopify.designMode) {
      // Editing a lab result block in the customizer opens the modal on
      // that flavor; deselecting closes it again.
      this.onBlockSelect = (event) => {
        const i = this.panels.indexOf(event.target);
        if (i === -1) return;
        const dialog = this.closest('dialog');
        if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
          if (window.WildgainzModal && window.WildgainzModal.open) window.WildgainzModal.open(dialog);
          else dialog.showModal();
        }
        this.select(i);
      };
      this.onBlockDeselect = (event) => {
        if (!this.contains(event.target)) return;
        this.closest('dialog')?.close();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  disconnectedCallback() {
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  select(index, focus = false) {
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.classList.toggle('wg-transparency__pill--active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
    if (focus && this.tabs[index]) this.tabs[index].focus();
  }

  onKeydown(event, index) {
    const count = this.tabs.length;
    if (count < 2) return;
    let target;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        target = (index + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        target = (index - 1 + count) % count;
        break;
      case 'Home':
        target = 0;
        break;
      case 'End':
        target = count - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.select(target, true);
  }
}

if (!customElements.get('wildgainz-lab-results')) {
  customElements.define('wildgainz-lab-results', WildgainzLabResults);
}

if (window.Shopify && window.Shopify.designMode && !window.__wgTransparencyCheckBound) {
  // Selecting a buyer's guide check block in the customizer opens that
  // check, so the merchant sees the text they are editing. Bound once
  // even if the editor re-executes this script.
  window.__wgTransparencyCheckBound = true;
  document.addEventListener('shopify:block:select', (event) => {
    const check = event.target.closest('details.wg-transparency__check');
    if (check) check.open = true;
  });
}
})();
